/**
 * Cross-tab session notifications. Every tab of a browser profile shares one session cookie, but each tab keeps
 * its own React identity and unsaved forms. When one tab signs in or out, the others must learn about it before
 * they send anything, so a form typed as user A is never saved with user B's cookie.
 *
 * Transport: BroadcastChannel('esaas-session'), plus a localStorage 'storage'-event fallback for browsers or
 * contexts without BroadcastChannel. Messages carry a per-tab origin id (own messages are ignored) and a nonce
 * (a message seen on both transports is handled once).
 */
export type SessionSignalKind='login'|'logout';
export type SessionSignal={kind:SessionSignalKind;tenantId:string;userId:string;nonce:string;origin:string};
export type SessionActor={tenantId:string;userId:string};

type ChannelLike={postMessage(data:unknown):void;close():void;onmessage:((event:{data:unknown})=>void)|null};
type StorageLike={setItem(key:string,value:string):void;removeItem(key:string):void};
type WindowLike={addEventListener(type:'storage',fn:(event:{key:string|null;newValue:string|null})=>void):void;removeEventListener(type:'storage',fn:(event:{key:string|null;newValue:string|null})=>void):void};
export type SessionSyncDeps={channel?:()=>ChannelLike|null;storage?:()=>StorageLike|null;win?:()=>WindowLike|null;tabId?:string};

export const SESSION_CHANNEL='esaas-session';
export const SESSION_STORAGE_KEY='esaas.session.signal';

const randomId=()=>globalThis.crypto?.randomUUID?.()??`${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
export const TAB_ID=randomId();

const defaultDeps:Required<Omit<SessionSyncDeps,'tabId'>>={
 channel:()=>{try{return typeof BroadcastChannel==='undefined'?null:new BroadcastChannel(SESSION_CHANNEL) as unknown as ChannelLike}catch{return null}},
 storage:()=>{try{return typeof window==='undefined'?null:window.localStorage}catch{return null}},
 win:()=>typeof window==='undefined'?null:window,
};

// One long-lived sending channel per tab: closing a channel right after postMessage is not needed and keeps
// delivery semantics simple. (A BroadcastChannel never receives its own messages.)
let sendChannel:ChannelLike|null|undefined;
const publisher=()=>{if(sendChannel===undefined)sendChannel=defaultDeps.channel();return sendChannel};

export function isSessionSignal(value:unknown):value is SessionSignal{
 if(typeof value!=='object'||value===null)return false;
 const v=value as Record<string,unknown>;
 return (v.kind==='login'||v.kind==='logout')&&typeof v.tenantId==='string'&&typeof v.userId==='string'&&typeof v.nonce==='string'&&typeof v.origin==='string';
}

/** Tells the other tabs that this tab signed in (as `actor`) or signed out. Never throws. */
export function publishSession(kind:SessionSignalKind,actor:SessionActor|null,deps:SessionSyncDeps={}):SessionSignal{
 const signal:SessionSignal={kind,tenantId:actor?.tenantId??'',userId:actor?.userId??'',nonce:randomId(),origin:deps.tabId??TAB_ID};
 try{const ch=deps.channel?deps.channel():publisher();ch?.postMessage(signal)}catch{/* channel unavailable */}
 try{
  const store=(deps.storage??defaultDeps.storage)();
  // setItem fires 'storage' in the other tabs only; removing it again keeps nothing behind and lets an identical
  // follow-up signal fire again.
  if(store){store.setItem(SESSION_STORAGE_KEY,JSON.stringify(signal));store.removeItem(SESSION_STORAGE_KEY)}
 }catch{/* storage unavailable */}
 return signal;
}

/** Calls `handler` for every session signal from another tab. Returns an unsubscribe function. */
export function subscribeSession(handler:(signal:SessionSignal)=>void,deps:SessionSyncDeps={}):()=>void{
 const self=deps.tabId??TAB_ID;
 const seen:string[]=[];
 const deliver=(value:unknown)=>{
  if(!isSessionSignal(value)||value.origin===self||seen.includes(value.nonce))return;
  seen.push(value.nonce);if(seen.length>50)seen.shift();
  handler(value);
 };
 let channel:ChannelLike|null=null;
 try{channel=(deps.channel??defaultDeps.channel)();if(channel)channel.onmessage=event=>deliver(event.data)}catch{channel=null}
 const win=(deps.win??defaultDeps.win)();
 const onStorage=(event:{key:string|null;newValue:string|null})=>{
  if(event.key!==SESSION_STORAGE_KEY||!event.newValue)return;
  try{deliver(JSON.parse(event.newValue))}catch{/* malformed */}
 };
 win?.addEventListener('storage',onStorage);
 return()=>{try{channel?.close()}catch{/* closed */}win?.removeEventListener('storage',onStorage)};
}

export type SessionSignalAction='ignore'|'switched'|'signed-out';
/**
 * What a tab currently signed in as `currentKey` (`tenantId|userId`, empty when signed out) must do with a signal.
 * A same-user login (re-login, session refresh) is ignored so forms and the branch stay as they are.
 */
export function classifySessionSignal(currentKey:string,signal:SessionSignal):SessionSignalAction{
 if(!currentKey)return 'ignore';
 if(signal.kind==='logout')return 'signed-out';
 return `${signal.tenantId}|${signal.userId}`===currentKey?'ignore':'switched';
}
